import React, { useCallback } from 'react';
import { useI18n } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { preferencesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Globe } from 'lucide-react';

export const LanguageSwitcher = ({
  variant = 'dropdown',
  className = '',
  dark = false,
  compact = false,
  inline = false,
}) => {
  const { language, setLanguage, languages } = useI18n();
  const { user, updateUserPreferences } = useAuth();

  const currentLang = languages.find((l) => l.code === language);

  const handleLanguageChange = useCallback(
    async (newLang) => {
      if (newLang === language) return;
      setLanguage(newLang, { persist: true });
      if (user) {
        updateUserPreferences({ language: newLang });
        try {
          await preferencesAPI.update({ language: newLang });
        } catch (err) {
          console.error('Failed to save language preference:', err);
        }
      }
    },
    [language, setLanguage, user, updateUserPreferences],
  );

  const inactiveBtnClass = dark
    ? 'text-slate-300 hover:text-white hover:bg-[#0A2F52]'
    : '';

  if (variant === 'buttons') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {languages.map((lang) => (
          <Button
            key={lang.code}
            type="button"
            variant={language === lang.code ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleLanguageChange(lang.code)}
            className={
              language === lang.code
                ? 'bg-domio-600 hover:bg-domio-700'
                : inactiveBtnClass
            }
          >
            {lang.flag} {lang.code.toUpperCase()}
          </Button>
        ))}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          className={`${
            compact
              ? 'h-9 w-9'
              : inline
                ? 'h-9 w-auto shrink-0 px-2.5 gap-1.5 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                : dark
                  ? 'h-9 w-auto shrink-0 px-2 gap-1.5'
                  : 'w-full justify-start gap-2'
          } ${dark ? 'text-slate-300 hover:text-white hover:bg-[#0A2F52]' : ''} ${className}`}
          title={currentLang ? currentLang.name : undefined}
        >
          <Globe className="h-4 w-4 shrink-0" />
          {!compact && (
            <span className="truncate font-semibold text-sm tabular-nums">
              {currentLang?.code.toUpperCase()}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={compact ? 'center' : 'start'} side={compact ? 'right' : 'top'}>
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={language === lang.code ? 'bg-domio-50 text-domio-600' : ''}
          >
            <span className="mr-2">{lang.flag}</span>
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
